import { Identifiable, Reference } from '@approvalmax/types';
import {
    CheckboxEditor,
    DateTimeEditor,
    DropdownEditor,
    NumberEditor,
    prioritizeAndMapDropdownItems,
    TextEditor,
} from '@approvalmax/ui';
import { Text, Tooltip } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { MemoSection } from 'pages/requestForm/components';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { usePrevious } from 'react-use';
import { useNetSuiteBillFieldSettings } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import { SectionContainer } from '../../../../../components/SectionContainer';
import { NetSuiteContext, Vendor } from '../../../../../data/netsuite/NetSuiteContext';
import { useGetRecognizedOcrAttachment } from '../../../../../hooks/useGetRecognizedOcrAttachment';
import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import {
    NetSuiteAccountsDataProvider,
    NetSuiteCurrencySection,
    NetSuiteCustomFields,
    NetSuiteVendorsDataProvider,
} from '../../../components';
import { mapAddress } from '../../../helpers';
import { useCurrencyPrecision, useVendorData } from '../../../hooks';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { useNetSuiteBillFields } from '../../NetSuiteBillRequestForm.hooks';
import { netSuiteBillFieldValuesState } from '../../NetSuiteBillRequestForm.states';
import { NetSuiteBillFields } from '../../NetSuiteBillRequestForm.types';
import { messages } from './NetSuiteBillFieldsSection.messages';
import { FieldsWrapper, StyledField } from './NetSuiteBillFieldsSection.styles';
import { NetSuiteBillFieldsSectionProps } from './NetSuiteBillFieldsSection.types';

const { netSuiteConstants } = constants;

const NetSuiteBillFieldsSection: FC<NetSuiteBillFieldsSectionProps> = (props) => {
    const {
        requestId,
        companyId,
        workflowId,
        discountAmount,
        discountDate,
        initialMemoValue,
        savedVendorId,
        canCreateVendor,
        onNewVendorClick,
        showExpensesTable,
        updateExpenseItemsOnCurrencyChange,
        updateItemsOnCurrencyChange,
        updateExpenseItemsAccount,
    } = props;

    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const fieldValues = useAtomValue(netSuiteBillFieldValuesState);
    const showErrors = useAtomValue(netSuiteShowErrorsState);

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const request = useSelector((state) => selectors.request.getRequestById(state, requestId));

    const { currencies, terms, documentFields } = netSuiteEditingContext;

    const { onFieldChange, updateFieldsBulk } = useNetSuiteBillFields();

    const { vendorData, isLoading: isVendorDataLoading } = useVendorData(
        currencies,
        fieldValues.vendor?.id,
        requestId,
        companyId
    );

    const prevVendorData = usePrevious(vendorData);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const showVendorWarning = ocrData?.document?.vendorIds && ocrData.document.vendorIds.length > 1;
    const showDueDateWarning = ocrAttachment && moment(fieldValues.dueDate).isBefore(fieldValues.date);
    const showDepartmentWarning = ocrData?.document?.departmentIds && ocrData.document?.departmentIds.length > 1;

    let sortedDepartments: Identifiable[] | Reference[] = netSuiteEditingContext.departments;

    if (ocrAttachment && ocrData) {
        sortedDepartments = prioritizeAndMapDropdownItems(sortedDepartments, ocrData?.document?.departmentIds);
    }

    useEffect(() => {
        /**
         * Do not update fields with default values from vendor data
         * if it was first fetch for saved vendor
         * as in this case prefilled fields from request
         * will be rewritten
         */
        if (!prevVendorData && vendorData && vendorData.id === savedVendorId) {
            return;
        }

        if (prevVendorData?.id === vendorData?.id) {
            return;
        }

        if (vendorData) {
            updateFieldsBulk({
                address: vendorData.defaultBillingAddress,
                currency: vendorData.currency,
                terms: vendorData.terms,
                account: vendorData.defaultPayablesAccount,
            });

            showExpensesTable && updateExpenseItemsAccount(vendorData.defaultExpenseAccount);
        }
    }, [vendorData, updateFieldsBulk, prevVendorData, savedVendorId, showExpensesTable, updateExpenseItemsAccount]);

    const fieldSettings = useNetSuiteBillFieldSettings(documentFields);

    const onEditorChange =
        <K extends keyof NetSuiteBillFields>(key: K) =>
        (value: NetSuiteBillFields[K]) =>
            onFieldChange(key, value);

    const onVendorChange = useCallback(
        (vendor: Vendor | null) => {
            updateFieldsBulk({
                vendor: vendor ?? undefined,
                vatRegistrationNumber: vendor?.taxNumber,
                address: null,
                currency: null,
            });
        },
        [updateFieldsBulk]
    );

    const onCurrencyChange = useCallback(
        (currency: NetSuiteContext['currencies'][0]) => {
            updateItemsOnCurrencyChange(currency);
            updateExpenseItemsOnCurrencyChange(fieldValues.currency, currency);

            updateFieldsBulk({
                currency,
                exchangeRate: null,
            });
        },
        [updateFieldsBulk, updateItemsOnCurrencyChange, updateExpenseItemsOnCurrencyChange, fieldValues.currency]
    );

    const onDateChange = useCallback(
        (date: string) => {
            const dueDate = moment(date);

            if (fieldValues.terms) {
                const selectedTerm = terms.find((term) => term.id === fieldValues.terms?.id);

                dueDate.add(selectedTerm?.daysUntilNetDue ?? 0, 'd');
            }

            updateFieldsBulk({
                date,
                dueDate: fieldSettings.dueDate === domain.DocumentFieldState.Hidden ? null : dueDate.toISOString(),
            });
        },
        [fieldValues.terms, fieldSettings, terms, updateFieldsBulk]
    );

    const onTermsChange = useCallback(
        (newTerms: NetSuiteContext['terms'][0]) => {
            if (fieldValues.date) {
                const dueDate = moment(fieldValues.date);

                dueDate.add(newTerms?.daysUntilNetDue ?? 0, 'd');

                updateFieldsBulk({
                    terms: newTerms,
                    dueDate: fieldSettings.dueDate === domain.DocumentFieldState.Hidden ? null : dueDate.toISOString(),
                });
            } else {
                updateFieldsBulk({
                    terms: newTerms,
                });
            }
        },
        [fieldValues.date, fieldSettings, updateFieldsBulk]
    );

    const vendorCurrencies = useMemo(() => {
        return currencies.filter((currency) => vendorData?.currencyIds?.includes(currency.id));
    }, [vendorData, currencies]);

    const checkCanCreateVendor = useCallback(() => {
        return canCreateVendor;
    }, [canCreateVendor]);

    const createVendorDisplayTextFn = useCallback(() => {
        return messages.newVendor;
    }, []);

    const onChangeMemo = useCallback((value: string) => onFieldChange('memo', value), [onFieldChange]);

    const onChangeAddress = (value: domain.BillingAddress | null) => {
        onFieldChange('address', mapAddress(value));
    };

    return (
        <>
            <SectionContainer title={messages.vendorDetailsSectionName}>
                <FieldsWrapper>
                    <StyledField title={messages.nsVendorFieldText} required>
                        <Tooltip
                            disabled={!showVendorWarning}
                            placement='top'
                            width='max-content'
                            activator={
                                <NetSuiteVendorsDataProvider>
                                    <DropdownEditor
                                        warning={showVendorWarning}
                                        value={fieldValues.vendor || null}
                                        singleLinePerItem={false}
                                        onChange={onVendorChange}
                                        placeholder={messages.nsVendorFieldPlaceholder}
                                        invalid={showErrors && !fieldValues.vendor}
                                        canCreateItemFn={checkCanCreateVendor}
                                        createItemDisplayTextFn={createVendorDisplayTextFn}
                                        onCreateItem={onNewVendorClick}
                                        disabled={ocrIsLoading}
                                    />
                                </NetSuiteVendorsDataProvider>
                            }
                        >
                            <Text spacing='8' font='body' fontSize='small'>
                                {messages.multipleVendorsWarning}
                            </Text>
                        </Tooltip>
                    </StyledField>

                    {fieldSettings.billingAddress !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsVendorBillingAddress}
                            required={fieldSettings.billingAddress === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={vendorData?.address ?? []}>
                                <DropdownEditor
                                    value={fieldValues.address || null}
                                    singleLinePerItem={false}
                                    onChange={onChangeAddress}
                                    placeholder={messages.nsVendorBillingAddressPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.address &&
                                        fieldSettings.billingAddress === domain.DocumentFieldState.Mandatory
                                    }
                                    loading={isVendorDataLoading}
                                    displayAttribute='fullAddress'
                                    disabled={
                                        fieldSettings.billingAddress === domain.DocumentFieldState.ReadOnly ||
                                        ocrIsLoading
                                    }
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.vatRegistration !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsVATRegistration}
                            required={fieldSettings.vatRegistration === domain.DocumentFieldState.Mandatory}
                        >
                            <TextEditor
                                disabled={
                                    fieldSettings.vatRegistration === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                }
                                value={fieldValues.vatRegistrationNumber || ''}
                                onChange={onEditorChange('vatRegistrationNumber')}
                                placeholder={messages.textFieldPlaceholder}
                                invalid={
                                    showErrors &&
                                    !fieldValues.vatRegistrationNumber &&
                                    fieldSettings.vatRegistration === domain.DocumentFieldState.Mandatory
                                }
                            />
                        </StyledField>
                    )}
                </FieldsWrapper>
            </SectionContainer>

            <NetSuiteCurrencySection
                currencies={vendorCurrencies}
                fieldValues={fieldValues}
                fieldSettings={fieldSettings}
                expectedCurrencyIsoCode={ocrData?.document?.currencyCode}
                isLoadingCurrencies={isVendorDataLoading}
                onExchangeRateChange={onEditorChange('exchangeRate')}
                onCurrencyChange={onCurrencyChange}
            />

            <SectionContainer title={messages.billDetailsSectionName}>
                <FieldsWrapper>
                    {fieldSettings.reference !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsReferenceFieldText}
                            required={fieldSettings.reference === domain.DocumentFieldState.Mandatory}
                        >
                            <TextEditor
                                value={fieldValues.reference || ''}
                                onChange={onEditorChange('reference')}
                                placeholder={messages.textFieldPlaceholder}
                                changeOnBlur
                                maxLength={netSuiteConstants.referenceMaxLength}
                                disabled={
                                    fieldSettings.reference === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                }
                                invalid={
                                    showErrors &&
                                    !fieldValues.reference &&
                                    fieldSettings.reference === domain.DocumentFieldState.Mandatory
                                }
                            />
                        </StyledField>
                    )}

                    {fieldSettings.terms !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsTermsFieldText}
                            required={fieldSettings.terms === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.terms}>
                                <DropdownEditor
                                    value={fieldValues.terms || null}
                                    singleLinePerItem={false}
                                    onChange={onTermsChange}
                                    placeholder={messages.nsTermsFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.terms &&
                                        fieldSettings.terms === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={
                                        fieldSettings.terms === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                    }
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    <StyledField title={messages.nsDateFieldText} required>
                        <DateTimeEditor
                            value={fieldValues.date || null}
                            placeholder={messages.nsDateFieldPlaceholder}
                            invalid={showErrors && !fieldValues.date}
                            onChange={onDateChange}
                            disabled={ocrIsLoading}
                        />
                    </StyledField>

                    {fieldSettings.dueDate !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsDueDateFieldText}
                            required={fieldSettings.dueDate === domain.DocumentFieldState.Mandatory}
                        >
                            <Tooltip
                                disabled={!showDueDateWarning}
                                placement='top'
                                width='max-content'
                                activator={
                                    <DateTimeEditor
                                        warning={showDueDateWarning}
                                        value={fieldValues.dueDate || null}
                                        placeholder={messages.nsDueDateFieldPlaceholder}
                                        onChange={onEditorChange('dueDate')}
                                        disabled={
                                            fieldSettings.dueDate === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                        }
                                        invalid={
                                            showErrors &&
                                            !fieldValues.dueDate &&
                                            fieldSettings.dueDate === domain.DocumentFieldState.Mandatory
                                        }
                                    />
                                }
                            >
                                <Text spacing='8' font='body' fontSize='small'>
                                    {messages.earlyDueDateWarning}
                                </Text>
                            </Tooltip>
                        </StyledField>
                    )}

                    {fieldSettings.discountAmount !== domain.DocumentFieldState.Hidden && (
                        <StyledField title={messages.nsDiscountAmountFieldText}>
                            <NumberEditor
                                allowFloat
                                value={discountAmount}
                                onChange={onEditorChange('discountAmount')}
                                precision={currencyPrecision}
                                disabled
                                placeholder=' '
                                displayFormat='currency'
                            />
                        </StyledField>
                    )}

                    {fieldSettings.discountDate !== domain.DocumentFieldState.Hidden && (
                        <StyledField title={messages.nsDiscountDateFieldText}>
                            <DateTimeEditor
                                value={discountDate || null}
                                placeholder=''
                                onChange={onEditorChange('discountDate')}
                                disabled
                            />
                        </StyledField>
                    )}

                    {fieldSettings.paymentHold !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsPaymentHoldFieldText}
                            required={fieldSettings.paymentHold === domain.DocumentFieldState.Mandatory}
                        >
                            <CheckboxEditor
                                onChange={onEditorChange('isPaymentHold')}
                                value={fieldValues.isPaymentHold || false}
                                disabled={
                                    fieldSettings.paymentHold === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                }
                            >
                                {messages.nsPaymentHoldYes}
                            </CheckboxEditor>
                        </StyledField>
                    )}

                    {fieldSettings.account !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsAccountFieldText}
                            required={fieldSettings.account === domain.DocumentFieldState.Mandatory}
                        >
                            <NetSuiteAccountsDataProvider>
                                <DropdownEditor
                                    value={fieldValues.account || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('account')}
                                    placeholder={messages.nsAccountFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.account &&
                                        fieldSettings.account === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={
                                        fieldSettings.account === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                    }
                                    loading={false}
                                />
                            </NetSuiteAccountsDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.department !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsDepartmentFieldText}
                            required={fieldSettings.department === domain.DocumentFieldState.Mandatory}
                        >
                            <Tooltip
                                disabled={!showDepartmentWarning}
                                placement='top'
                                width='max-content'
                                activator={
                                    <PlainDataProvider items={sortedDepartments}>
                                        <DropdownEditor
                                            warning={showDepartmentWarning}
                                            value={fieldValues.department || null}
                                            singleLinePerItem={false}
                                            onChange={onEditorChange('department')}
                                            placeholder={messages.nsDepartmentFieldPlaceholder}
                                            invalid={
                                                showErrors &&
                                                !fieldValues.department &&
                                                fieldSettings.department === domain.DocumentFieldState.Mandatory
                                            }
                                            disabled={
                                                fieldSettings.department === domain.DocumentFieldState.ReadOnly ||
                                                ocrIsLoading
                                            }
                                            loading={false}
                                        />
                                    </PlainDataProvider>
                                }
                            >
                                <Text spacing='8' font='body' fontSize='small'>
                                    {messages.multipleDepartmentsWarning}
                                </Text>
                            </Tooltip>
                        </StyledField>
                    )}

                    {fieldSettings.class !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsClassFieldText}
                            required={fieldSettings.class === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.classes}>
                                <DropdownEditor
                                    value={fieldValues.class || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('class')}
                                    placeholder={messages.nsClassFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.class &&
                                        fieldSettings.class === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={
                                        fieldSettings.class === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                    }
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {fieldSettings.location !== domain.DocumentFieldState.Hidden && (
                        <StyledField
                            title={messages.nsLocationFieldText}
                            required={fieldSettings.location === domain.DocumentFieldState.Mandatory}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.locations}>
                                <DropdownEditor
                                    value={fieldValues.location || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('location')}
                                    placeholder={messages.nsLocationFieldPlaceholder}
                                    invalid={
                                        showErrors &&
                                        !fieldValues.location &&
                                        fieldSettings.location === domain.DocumentFieldState.Mandatory
                                    }
                                    disabled={
                                        fieldSettings.location === domain.DocumentFieldState.ReadOnly || ocrIsLoading
                                    }
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} />
                </FieldsWrapper>
            </SectionContainer>

            {fieldSettings.memo !== domain.DocumentFieldState.Hidden && (
                <MemoSection
                    defaultValue={initialMemoValue ?? ''}
                    onChange={onChangeMemo}
                    placeholder={
                        fieldSettings.memo === domain.DocumentFieldState.Editable ? messages.textFieldPlaceholder : ''
                    }
                    maxLength={netSuiteConstants.memoMaxLength}
                    disabled={fieldSettings.memo === domain.DocumentFieldState.ReadOnly || ocrIsLoading}
                    invalid={
                        showErrors && !fieldValues.memo && fieldSettings.memo === domain.DocumentFieldState.Mandatory
                    }
                    width={565}
                />
            )}
        </>
    );
};

export default NetSuiteBillFieldsSection;
